import { useCallback, useState } from 'react';
import SentryAPI from '../../../api/SentryAPI';
import { get } from '../../../api';

const base64ToBlob = (base64) => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays);

  return blob;
};

const isValidFilename = (filename) => {
  const regex = /^[a-zA-Z0-9-_. ]{1,255}$/;
  return regex.test(filename);
};

const isFileValid = (file) => {
  const isNotValidFile =
    !/^application\/(pdf|msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/.test(file.type);

  if (file.size / 1024 > 10000 || isNotValidFile) {
    return false;
  }

  if (!isValidFilename(file.name)) {
    return false;
  }

  return true;
};

export const useFileUpload = ({ network, baseURL }) => {
  const [resumeError, setResumeError] = useState(false);

  const uploadFileToS3 = useCallback(
    async (file, presignedUrl) => {
      try {
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });
        if (uploadResponse.statusText !== 'OK') {
          SentryAPI.trackError('Failed to upload file to S3', {
            server: true,
            networkId: network?.id || undefined,
            filename: file.name,
          });
        }
      } catch (error) {
        SentryAPI.trackError('Failed to upload file to S3', {
          server: true,
          networkId: network?.id || undefined,
          filename: file.name || undefined,
          error,
        });
      }
    },
    [network],
  );

  const uploadFile = useCallback(
    async (file, setFieldValue) => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        const response = await get(
          `/collections/${network.id}/job_seekers_resume?resume_file_name=${file.name}`,
          { baseURL },
          headers,
        );

        if (response.data.presignedUrl) {
          if (typeof file?.content === 'string') {
            const blob = base64ToBlob(file.content);
            await uploadFileToS3(blob, response.data.presignedUrl);
          } else {
            await uploadFileToS3(file, response.data.presignedUrl);
          }

          setFieldValue('resumeUrl', response.data.resumeUrl);
        } else {
          SentryAPI.trackError('Error getting presigned URL', {
            server: true,
            networkId: network?.id || undefined,
            responseErrors: response.errors || undefined,
          });
        }
      } catch (error) {
        SentryAPI.trackError('Error fetching presigned URL', {
          server: true,
          networkId: network?.id || undefined,
          error,
        });
      }
    },
    [baseURL, network.id, uploadFileToS3],
  );

  const handleFileChange = async (e, setFieldValue) => {
    const file = e.target.files[0];
    const fileNotValid = !isFileValid(file);

    if (fileNotValid) {
      setResumeError(true);
      e.stopPropagation();
      return;
    }

    setResumeError(false);

    setFieldValue('cvUrl', [{ name: file.name }]);
    uploadFile(file, setFieldValue);
  };

  return { base64ToBlob, uploadFile, resumeError, handleFileChange };
};
